import { useState, useEffect } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  LayoutDashboard, Boxes, Gauge, ArrowDownToLine, ArrowUpFromLine, Undo2,
  ListChecks, MoveRight, AlertTriangle, PackageX, DollarSign, ScanLine,
  Radio, Users, TrendingUp, Loader2,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

function KPI({ icon: Icon, label, value, tone = "blue", sub }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600", red: "bg-red-50 text-red-600",
    purple: "bg-violet-50 text-violet-600", slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow fade-up">
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tones[tone]} dark:bg-opacity-20`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        {sub && <span className="text-[11px] font-medium text-slate-400">{sub}</span>}
      </div>
      <div className="mt-3 font-display text-2xl font-bold text-slate-900 dark:text-foreground">{value}</div>
      <div className="text-sm text-slate-500 dark:text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/dashboard"); setData(data); } catch {} })();
  }, []);

  if (!data) return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />Loading dashboard...</div>;

  const k = data.kpis;
  const fmt = (n) => new Intl.NumberFormat().format(n);

  return (
    <div>
      <PageHeader title="Operations Dashboard" subtitle="Real-time overview of your warehouse network"
        icon={LayoutDashboard} breadcrumb={["Home", "Dashboard"]} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI icon={Boxes} label="Total Inventory" value={fmt(k.total_inventory)} tone="blue" sub={`${k.total_items} SKUs`} />
        <KPI icon={Gauge} label="Warehouse Utilization" value={`${k.warehouse_utilization}%`} tone="purple" sub={`${k.total_warehouses} sites`} />
        <KPI icon={ArrowDownToLine} label="Today's Inward" value={fmt(k.today_inward)} tone="green" />
        <KPI icon={ArrowUpFromLine} label="Today's Outward" value={fmt(k.today_outward)} tone="amber" />
        <KPI icon={DollarSign} label="Inventory Value" value={`₹${fmt(k.inventory_value)}`} tone="green" />
        <KPI icon={AlertTriangle} label="Low Stock Items" value={fmt(k.low_stock)} tone="red" />
        <KPI icon={PackageX} label="Dead Stock" value={fmt(k.dead_stock)} tone="slate" />
        <KPI icon={Undo2} label="Returns" value={fmt(k.returns)} tone="amber" />
        <KPI icon={ListChecks} label="Pending Picking" value={fmt(k.pending_picking)} tone="blue" />
        <KPI icon={MoveRight} label="Pending Put-Away" value={fmt(k.pending_putaway)} tone="purple" />
        <KPI icon={ScanLine} label="QR Scans Today" value={fmt(k.qr_scans_today)} tone="blue" />
        <KPI icon={Radio} label="RFID Reads" value={fmt(k.rfid_reads_today)} tone="green" />
      </div>

      {/* charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" />Inventory Trend (7 days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.charts.inventory_trend}>
              <defs>
                <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} /><stop offset="95%" stopColor="#2563eb" stopOpacity={0} /></linearGradient>
                <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <Tooltip />
              <Area type="monotone" dataKey="inward" stroke="#2563eb" fill="url(#gIn)" strokeWidth={2} />
              <Area type="monotone" dataKey="outward" stroke="#10b981" fill="url(#gOut)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Stock Movement</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.charts.stock_movement} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {data.charts.stock_movement.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Warehouse Occupancy</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.warehouse_occupancy} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="utilization" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Monthly Transactions</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.charts.monthly_transactions}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <Tooltip />
              <Line type="monotone" dataKey="transactions" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Return Analysis</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.return_analysis}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="reason" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* activity + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Recent Activity</h3>
          <div className="space-y-1">
            {data.recent_activities.length === 0 && <p className="text-sm text-slate-400">No recent activity.</p>}
            {data.recent_activities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2 border-b border-slate-50 dark:border-border last:border-0">
                <StatusBadge status={a.action} />
                <span className="text-sm text-slate-700 dark:text-foreground flex-1">{a.description}</span>
                <span className="text-xs text-slate-400">{a.user_name}</span>
                <span className="text-xs text-slate-300 hidden sm:block">{new Date(a.timestamp).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Alerts</h3>
          <div className="space-y-2.5">
            {data.alerts.length === 0 && <p className="text-sm text-slate-400">No active alerts.</p>}
            {data.alerts.map((al) => (
              <div key={al.id} className="p-3 rounded-md bg-slate-50 dark:bg-secondary border border-slate-100 dark:border-border">
                <div className="flex items-center gap-2 mb-1"><StatusBadge status={al.severity} /><span className="text-sm font-medium text-slate-800 dark:text-foreground">{al.title}</span></div>
                <p className="text-xs text-slate-500">{al.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
